import { ReactNode, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { FocusTrap } from 'focus-trap-react';
import { X } from 'lucide-react';

// tabbable@6 (used by focus-trap-react) emits a `:not([inert] *)` selector that
// throws SyntaxError in older in-app browsers / WebViews lacking complex `:not()`
// support. Skip the trap there; the dialog is still labeled and keyboard-closable.
const supportsComplexNotSelector = (() => {
  if (typeof document === 'undefined') return true;
  try {
    document.querySelector(':not([inert] *)');
    return true;
  } catch {
    return false;
  }
})();

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  maxWidth?: string;
  showCloseButton?: boolean;
  closeOnOverlayClick?: boolean;
  className?: string;
}

/**
 * Reusable modal component with animation and accessibility features.
 * Used across Profile, BasicResults, AssessmentChoice pages.
 */
export function Modal({
  isOpen,
  onClose,
  title,
  children,
  maxWidth = '480px',
  showCloseButton = true,
  closeOnOverlayClick = true,
  className = ''
}: ModalProps) {
  const reduceMotion = useReducedMotion() ?? false;
  const dialogRef = useRef<HTMLDivElement>(null);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open; preserve layout width when scrollbar disappears
  useEffect(() => {
    if (isOpen) {
      const w = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden';
      if (w > 0) document.body.style.paddingRight = `${w}px`;
    } else {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    };
  }, [isOpen]);

  const dialog = (
    <motion.div
      ref={dialogRef}
      // Loading / progress modals often have no buttons; tabIndex keeps FocusTrap valid.
      tabIndex={-1}
      initial={reduceMotion ? false : { opacity: 0, scale: 0.95, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={reduceMotion ? undefined : { opacity: 0, scale: 0.95, y: 20 }}
      transition={{ duration: reduceMotion ? 0 : 0.25 }}
      onClick={(e) => e.stopPropagation()}
      style={{
        borderRadius: '24px',
        padding: '32px',
        maxWidth,
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto',
        outline: 'none',
      }}
      className={['modal-theme-scope', className].filter(Boolean).join(' ')}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'modal-title' : undefined}
    >
      {(title || showCloseButton) && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px'
          }}
        >
          {title && (
            <h2
              id="modal-title"
              style={{
                fontSize: '20px',
                fontWeight: '600',
                color: 'var(--text-primary)'
              }}
            >
              {title}
            </h2>
          )}
          {showCloseButton && (
            <button
              onClick={onClose}
              style={{
                background: 'var(--surface-light)',
                border: '1px solid var(--border)',
                borderRadius: '8px',
                padding: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginLeft: 'auto',
              }}
              aria-label="Close modal"
            >
              <X size={20} color="var(--text-muted)" />
            </button>
          )}
        </div>
      )}
      {children}
    </motion.div>
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={reduceMotion ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={reduceMotion ? undefined : { opacity: 0 }}
          transition={{ duration: reduceMotion ? 0 : 0.2 }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(15, 23, 42, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px',
            backdropFilter: 'blur(4px)',
          }}
          onClick={closeOnOverlayClick ? onClose : undefined}
        >
          {supportsComplexNotSelector ? (
            <FocusTrap
              focusTrapOptions={{
                escapeDeactivates: false,
                clickOutsideDeactivates: false,
                // Default trap blocks outside clicks; overlay needs them for close-on-backdrop.
                allowOutsideClick: true,
                // StudioLaunchModal / PlanCreatingOverlay / ResolveLoadingModal open with
                // no tabbable controls; without this, focus-trap throws on activate.
                fallbackFocus: () => dialogRef.current ?? document.body,
              }}
            >
              {dialog}
            </FocusTrap>
          ) : (
            dialog
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default Modal;
